import { useEffect, useState } from "react";
import { Link as RouterLink, Routes, Route } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DashboardShell from "../../components/DashboardShell";
import SystemSideBar from "./SystemSideBar";
import AppSettingsPage from "../AppSettingsPage";
import SchoolsList from "./SchoolsList";
import SchoolDetail from "./SchoolDetail";
import SubscriptionPayments from "./SubscriptionPayments";
import {
    approveTrialExtension,
    approveSchoolVerification,
    fetchSystemDashboard,
    formatDate,
    rejectTrialExtension,
    rejectSchoolVerification,
    subscriptionStatusColor,
} from "../../utils/systemApi";
import { getPlanById, formatPriceMwk } from "../../config/subscriptionPlans";

function SystemHomePage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [reviewingId, setReviewingId] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const res = await fetchSystemDashboard();
                if (!cancelled) setData(res);
            } catch (e) {
                if (!cancelled) {
                    setError(e.response?.data?.message || e.message || "Could not load dashboard");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const totals = data?.totals || {};

    const statCards = [
        { label: "Registered schools", value: totals.schools ?? 0 },
        { label: "Active subscriptions", value: totals.active ?? 0 },
        { label: "On trial", value: totals.trialing ?? 0 },
        { label: "Expired", value: totals.expired ?? 0 },
        { label: "Unlimited plan", value: totals.unlimited ?? 0 },
        { label: "Payment pending review", value: totals.pendingPayment ?? 0 },
        { label: "Schools awaiting verification", value: totals.pendingVerification ?? 0 },
        { label: "Trial extension requests", value: totals.pendingTrialExtensionRequests ?? 0 },
    ];

    const handleApproveExtension = async (schoolId) => {
        setReviewingId(schoolId);
        try {
            await approveTrialExtension(schoolId);
            const res = await fetchSystemDashboard();
            setData(res);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not approve extension");
        } finally {
            setReviewingId("");
        }
    };

    const handleRejectExtension = async (schoolId) => {
        setReviewingId(schoolId);
        try {
            await rejectTrialExtension(schoolId);
            const res = await fetchSystemDashboard();
            setData(res);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not reject extension");
        } finally {
            setReviewingId("");
        }
    };

    const handleApproveVerification = async (schoolId) => {
        setReviewingId(schoolId);
        try {
            await approveSchoolVerification(schoolId);
            const res = await fetchSystemDashboard();
            setData(res);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not approve school");
        } finally {
            setReviewingId("");
        }
    };

    const handleRejectVerification = async (schoolId) => {
        setReviewingId(schoolId);
        try {
            await rejectSchoolVerification(schoolId);
            const res = await fetchSystemDashboard();
            setData(res);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not reject school");
        } finally {
            setReviewingId("");
        }
    };

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Platform overview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Track registered schools, subscription health, and recent billing activity.
                </Typography>
            </Box>

            <Grid container spacing={2}>
                {statCards.map(({ label, value }) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={label}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{value}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {(data?.pendingSchoolVerifications || []).length > 0 ? (
                <Card variant="outlined" sx={{ borderColor: "warning.main" }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            New school registrations
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Approve to start the school&apos;s free trial automatically. Reject if the registration looks invalid.
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>School</TableCell>
                                        <TableCell>Director</TableCell>
                                        <TableCell>Plan</TableCell>
                                        <TableCell>Registered</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.pendingSchoolVerifications.map((row) => {
                                        const id = row.id || row._id;
                                        return (
                                            <TableRow key={id}>
                                                <TableCell>
                                                    <Button
                                                        component={RouterLink}
                                                        to={`/System/schools/${id}`}
                                                        size="small"
                                                        sx={{ textTransform: "none", fontWeight: 600 }}
                                                    >
                                                        {row.schoolName}
                                                    </Button>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{row.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                                                </TableCell>
                                                <TableCell>{getPlanById(row.subscriptionPlan).name}</TableCell>
                                                <TableCell>{formatDate(row.verificationRequestedAt)}</TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            disabled={reviewingId === id}
                                                            onClick={() => handleApproveVerification(id)}
                                                        >
                                                            Approve & start trial
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            disabled={reviewingId === id}
                                                            onClick={() => handleRejectVerification(id)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            ) : null}

            {(data?.pendingTrialExtensions || []).length > 0 ? (
                <Card variant="outlined" sx={{ borderColor: "warning.main" }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Trial extension requests
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Each school may receive only one extension. Approve to grant 1 more month of trial access.
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>School</TableCell>
                                        <TableCell>Director</TableCell>
                                        <TableCell>Requested</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.pendingTrialExtensions.map((row) => {
                                        const id = row.id || row._id;
                                        return (
                                            <TableRow key={id}>
                                                <TableCell>
                                                    <Button
                                                        component={RouterLink}
                                                        to={`/System/schools/${id}`}
                                                        size="small"
                                                        sx={{ textTransform: "none", fontWeight: 600 }}
                                                    >
                                                        {row.schoolName}
                                                    </Button>
                                                </TableCell>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>{formatDate(row.trialExtensionRequestedAt)}</TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            disabled={reviewingId === id}
                                                            onClick={() => handleApproveExtension(id)}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            disabled={reviewingId === id}
                                                            onClick={() => handleRejectExtension(id)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            ) : null}

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h6">Recent registrations</Typography>
                                <Button component={RouterLink} to="/System/schools" size="small" endIcon={<ArrowForwardIcon />}>
                                    All schools
                                </Button>
                            </Stack>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>School</TableCell>
                                            <TableCell>Director</TableCell>
                                            <TableCell>Plan</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data?.recentRegistrations || []).map((row) => (
                                            <TableRow key={row.id || row._id}>
                                                <TableCell>{row.schoolName}</TableCell>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={getPlanById(row.subscriptionPlan).name} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h6">Recent subscription payments</Typography>
                                <Button component={RouterLink} to="/System/payments" size="small" endIcon={<ArrowForwardIcon />}>
                                    All payments
                                </Button>
                            </Stack>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>School</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data?.recentPayments || []).map((row) => (
                                            <TableRow key={row.id || row._id}>
                                                <TableCell>{row.schoolName}</TableCell>
                                                <TableCell>{formatPriceMwk(row.amount)}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" color={subscriptionStatusColor(row.status)} label={row.status} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Schools by plan</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        {Object.entries(data?.planBreakdown || {}).map(([planId, count]) => (
                            <Chip
                                key={planId}
                                label={`${getPlanById(planId).name}: ${count}`}
                                variant="outlined"
                            />
                        ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                        Total pupils across all schools: {totals.students ?? 0}
                    </Typography>
                </CardContent>
            </Card>
        </Stack>
    );
}

function SystemProfile() {
    const { currentUser } = useSelector((state) => state.user);
    return (
        <Card variant="outlined" sx={{ maxWidth: 480 }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Platform operator</Typography>
                <Typography variant="body2"><strong>Name:</strong> {currentUser?.name}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {currentUser?.email}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                    Last sign-in: {formatDate(currentUser?.lastLoginAt)}
                </Typography>
            </CardContent>
        </Card>
    );
}

export default function SystemDashboard() {
    return (
        <DashboardShell title="Platform console" sidebar={<SystemSideBar />} defaultDesktopOpen>
            <Routes>
                <Route index element={<SystemHomePage />} />
                <Route path="dashboard" element={<SystemHomePage />} />
                <Route path="schools" element={<SchoolsList />} />
                <Route path="schools/:schoolId" element={<SchoolDetail />} />
                <Route path="payments" element={<SubscriptionPayments />} />
                <Route path="profile" element={<SystemProfile />} />
                <Route path="app-settings" element={<AppSettingsPage />} />
            </Routes>
        </DashboardShell>
    );
}
